import type { AxiosProgressEvent } from "axios";
import axios from "../config/axios";
import { FileWithPath } from "react-dropzone";
import { ApiFile } from "../shared/interfaces";
import { FileUploadProps } from "../components/FileUpload";

export type UploadProgressCallback = (fileId: string, progress: number) => void;

export class FileUploader {
    /**
     * Upload a file with progress tracking
     * @param file The file to upload
     * @param onProgress Callback to track upload progress
     */
    public static async uploadFile(
        file: FileWithPath & { fileType?: string },
        onProgress?: UploadProgressCallback,
        marvinV2: boolean = false,
    ): Promise<ApiFile> {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("conversationId", "conversationId");

        // Add fileType if provided
        if (file.fileType) {
            formData.append("fileType", file.fileType);
        }

        // The shared axios instance, not a raw XHR: it carries the tab's bearer, the project route
        // header and the access headers, so the file lands in the project the URL names.
        const response = await axios.post<ApiFile>("/api/ai/files/upload", formData, {
            headers: marvinV2 ? { "X-Marvin-V2-Upload": "true" } : undefined,
            onUploadProgress: onProgress
                ? (event: AxiosProgressEvent) => {
                      if (event.total) {
                          const progress = Math.round((event.loaded / event.total) * 100);
                          onProgress(file.name + Date.now(), progress);
                      }
                  }
                : undefined,
        });
        return response.data;
    }

    /**
     * Create a FileUpload prop from a file
     * @param file The file to create props for
     * @param index The index of the file in the upload list
     */
    public static createFileUploadProps(file: File | FileWithPath, index: number): FileUploadProps {
        return {
            id: file.name + Date.now(),
            name: file.name,
            progress: 0,
            index: index,
            createdAt: new Date(),
            size: file.size,
        };
    }
}
